import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { brandingColors } from '@/mocks/registerRadio';
import { STEPS, generateSuggestedFrequency } from './types';
import type { RadioFormData } from './types';
import StepIndicator from './components/StepIndicator';
import RadioDataForm from './components/RadioDataForm';
import StreamUrlForm from './components/StreamUrlForm';
import LogoBrandingForm from './components/LogoBrandingForm';
import PlanSelection from './components/PlanSelection';
import FrequencyForm from './components/FrequencyForm';
import ReviewSummary from './components/ReviewSummary';
import SuccessScreen from './components/SuccessScreen';
import { SwalAlert } from '@/hooks/useSweetAlert';
import { createCheckout, redirectToCheckout } from '@/services/checkout';
import { getBillingPlanById } from '@/services/billing';
import type { BillingPeriod } from '@/types/checkout';

const initialFormData: RadioFormData = {
  name: '',
  slogan: '',
  description: '',
  category: '',
  country: '',
  city: '',
  streamUrl: '',
  streamType: '',
  bitrate: '',
  providerName: '',
  streamFormat: '',
  logoFile: null,
  logoPreview: '',
  accentColor: brandingColors[0].value,
  planId: '',
  frequency: '',
  specialFrequency: '',
  acceptedTerms: false,
  streamingMode: 'EXTERNAL_STREAM',
};

export default function RegisterRadioPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<RadioFormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'simulating' | 'done'>('idle');
  const [showSuccess, setShowSuccess] = useState(false);
  const [planPriceMonthly, setPlanPriceMonthly] = useState(0);
  const [planName, setPlanName] = useState('');
  const [planPriceDisplay, setPlanPriceDisplay] = useState('');

  const isFree = planPriceMonthly === 0;
  const isPaid = formData.planId !== '' && planPriceMonthly > 0;
  const isPremium = planPriceMonthly >= 89;

  // Resolve plan price when planId changes
  const resolvePlanPrice = async (planId: string) => {
    if (!planId) {
      setPlanPriceMonthly(0);
      setPlanName('');
      setPlanPriceDisplay('');
      return;
    }
    try {
      const plan = await getBillingPlanById(planId);
      setPlanPriceMonthly(plan?.price_monthly ?? 0);
      setPlanName(plan?.name ?? '');
      setPlanPriceDisplay(plan?.formatted_price_monthly ?? '');
    } catch {
      setPlanPriceMonthly(0);
      setPlanName('');
      setPlanPriceDisplay('');
    }
  };

  const updateField = (field: keyof RadioFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === 'planId') {
      resolvePlanPrice(value);
    }
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    if (step === 1) {
      if (!formData.name.trim()) newErrors.name = 'El nombre de la radio es obligatorio';
      if (!formData.category) newErrors.category = 'Selecciona una categoría';
      if (!formData.country) newErrors.country = 'Selecciona un país';
    }

    if (step === 2) {
      if (formData.streamingMode === 'EXTERNAL_STREAM') {
        if (!formData.streamUrl.trim()) newErrors.streamUrl = 'La URL del stream es obligatoria';
        else if (!/^https?:\/\/.+/.test(formData.streamUrl.trim()))
          newErrors.streamUrl = 'Ingresa una URL válida (http:// o https://)';
        if (!formData.streamType) newErrors.streamType = 'Selecciona el tipo de stream';
        if (!formData.bitrate) newErrors.bitrate = 'Selecciona el bitrate';
      }
      // RFW_MANAGED no requiere stream_url — pasa sin validación de stream
    }

    if (step === 4) {
      if (!formData.planId) newErrors.planId = 'Selecciona un plan';
    }

    if (step === 5) {
      if (!formData.acceptedTerms) {
        setErrors({ acceptedTerms: 'Debés aceptar los términos y condiciones para continuar.' });
        return false;
      }

      const allErrors: Record<string, string> = {};
      if (!formData.name.trim()) allErrors.name = 'El nombre es obligatorio';
      if (!formData.category) allErrors.category = 'Falta la categoría';
      if (!formData.country) allErrors.country = 'Falta el país';
      if (formData.streamingMode === 'EXTERNAL_STREAM') {
        if (!formData.streamUrl.trim()) allErrors.streamUrl = 'Falta la URL del stream';
        if (!formData.streamType) allErrors.streamType = 'Falta el tipo de stream';
      }
      if (!formData.planId) allErrors.planId = 'Falta el plan';
      if (Object.keys(allErrors).length > 0) {
        setErrors(allErrors);
        const firstErrorKey = Object.keys(allErrors)[0];
        const stepMap: Record<string, number> = {
          name: 1, category: 1, country: 1, slogan: 1,
          streamUrl: 2, streamType: 2, bitrate: 2,
          planId: 4,
        };
        setCurrentStep(stepMap[firstErrorKey] || 1);
        return false;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (!validateStep(currentStep)) return;

    if (currentStep < 5) {
      setCurrentStep((s) => s + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep((s) => s - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(5)) return;

    if (isFree) {
      SwalAlert.success('Tu solicitud quedó pendiente de aprobación.', 'Revisaremos tu radio en las próximas 24 a 48 horas. Recibirás un correo cuando esté activa.');
      setShowSuccess(true);
      return;
    }

    // ── Plan pago: checkout vía backend ──
    // El frontend SOLO solicita el checkout.
    // El backend devuelve la URL segura del procesador de pagos.
    // NUNCA se envía price, currency, payment_status ni subscription_status.

    const billingPeriod: BillingPeriod = 'monthly'; // Por ahora mensual; el selector anual podría parametrizarse

    try {
      const checkout = await createCheckout(
        formData.planId,
        billingPeriod,
        formData.streamingMode,
      );

      // Redirigir al usuario al checkout seguro del backend
      // Guardamos checkout_id + streamingMode para el retorno
      redirectToCheckout(checkout.checkout_url, checkout.checkout_id, formData.streamingMode);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error al iniciar el checkout.';

      SwalAlert.error(
        'No se pudo iniciar el checkout',
        message,
      );
    }
  };

  const startOver = () => {
    setShowSuccess(false);
    setFormData(initialFormData);
    setCurrentStep(1);
    setErrors({});
    setUploadStatus('idle');
    setUploadProgress(0);
  };

  // ── Success screen ──
  if (showSuccess) {
    return (
      <SuccessScreen
        formData={formData}
        isFree={isFree}
        isPaid={isPaid}
        onGoToAdmin={() => navigate('/admin/dashboard')}
        onGoHome={() => navigate('/')}
      />
    );
  }

  // ── Render step content ──
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <RadioDataForm formData={formData} updateField={updateField} errors={errors} />;
      case 2:
        return <StreamUrlForm formData={formData} updateField={updateField} errors={errors} />;
      case 3:
        return (
          <LogoBrandingForm
            formData={formData}
            updateField={updateField}
            setFormData={setFormData}
            errors={errors}
            setErrors={setErrors}
            uploadProgress={uploadProgress}
            uploadStatus={uploadStatus}
            setUploadProgress={setUploadProgress}
            setUploadStatus={setUploadStatus}
            isDragging={isDragging}
            setIsDragging={setIsDragging}
          />
        );
      case 4:
        return <PlanSelection formData={formData} updateField={updateField} errors={errors} />;
      case 5:
        return (
          <ReviewSummary
            formData={formData}
            setCurrentStep={setCurrentStep}
            isFree={isFree}
            isPremium={isPremium}
            planName={planName}
            planPriceDisplay={planPriceDisplay}
            onAcceptTerms={(accepted) => {
              setFormData((prev) => ({ ...prev, acceptedTerms: accepted }));
              if (errors.acceptedTerms) {
                setErrors((prev) => {
                  const next = { ...prev };
                  delete next.acceptedTerms;
                  return next;
                });
              }
            }}
            termsError={errors.acceptedTerms}
          />
        );
      default:
        return null;
    }
  };

  const isLastStep = currentStep === 5;
  const currentStepLabel = STEPS[currentStep - 1].label;

  return (
    <div className="min-h-screen bg-background-100/70 flex flex-col">
      {/* Intro hero */}
      {currentStep === 1 && (
        <div className="w-full max-w-2xl mx-auto px-4 pt-6 pb-2 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-100 text-accent-700 text-[11px] font-semibold mb-4">
            <i className="ri-radio-line"></i>
            Acceso por aprobación
          </div>
          <h1 className="text-xl md:text-2xl font-bold text-foreground-950 mb-2">
            Registrá tu radio en la red social más grande
          </h1>
          <p className="text-foreground-500 text-xs max-w-lg mx-auto leading-relaxed mb-2">
            Más de <strong className="text-foreground-700">11,300 frecuencias RFW</strong> disponibles. <strong className="text-foreground-800">Tu Frecuencia RFW será asignada automáticamente</strong> al completar el registro. Después, si querés, podés solicitar una frecuencia especial o premium. Cada emisora es revisada antes de ser aprobada. Una vez aceptada, accedés a tu página pública, cabina interactiva, QR personalizado y al directorio oficial.
          </p>
          <p className="text-foreground-500 text-xs max-w-lg mx-auto leading-relaxed mb-4">
            <strong className="text-foreground-700">Dos modalidades:</strong> conectá tu streaming externo o{' '}
            <strong className="text-accent-600">contratá streaming administrado por RadioFrecuenciaWeb</strong> con AutoDJ, almacenamiento, monitoreo y soporte incluidos.
          </p>
          <p className="text-foreground-400 text-[11px] max-w-lg mx-auto leading-relaxed mb-4">
            <strong className="text-red-500">Importante:</strong> No todas las solicitudes son aceptadas. RadioFrecuenciaWeb evalúa cada radio para garantizar calidad en el directorio. Completá el formulario con datos reales y tu stream funcionando para mejorar tus chances.
          </p>

          {/* Mini pipeline */}
          <div className="inline-flex items-center gap-3 bg-background-50 border border-background-200 rounded-xl px-4 py-2.5 mb-4">
            {[
              { icon: 'ri-file-add-line', label: 'Solicitud', color: 'text-sky-500' },
              { icon: 'ri-eye-line', label: 'Revisión SA', color: 'text-amber-500' },
              { icon: 'ri-check-double-line', label: 'Aprobación', color: 'text-emerald-500' },
              { icon: 'ri-play-circle-line', label: 'Radio activa', color: 'text-primary-500' },
            ].map((s, i) => (
              <div key={s.label} className="flex items-center gap-0">
                <div className="flex flex-col items-center gap-0.5">
                  <i className={`${s.icon} ${s.color} text-sm`}></i>
                  <span className="text-[9px] font-semibold text-foreground-500 whitespace-nowrap">{s.label}</span>
                </div>
                {i < 3 && <i className="ri-arrow-right-line text-foreground-300 text-[10px] ml-3"></i>}
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 text-[10px]">
            <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 font-semibold">
              <i className="ri-information-line"></i>
              Plan Free → siempre requiere aprobación
            </span>
            <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 font-semibold">
              <i className="ri-flashlight-line"></i>
              Planes pagos → activación rápida con revisión posterior
            </span>
          </div>

          <Link to="/radio-rules" className="inline-flex items-center gap-1 text-[11px] text-accent-600 hover:text-accent-700 font-medium cursor-pointer transition-colors mt-3">
            <i className="ri-information-line"></i>
            Cómo funciona el proceso de aprobación
          </Link>
        </div>
      )}

      {/* Minimal top bar */}
      <div className="w-full px-4 md:px-8 py-4 flex items-center justify-between">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-800 cursor-pointer transition-colors"
        >
          <i className="ri-arrow-left-line"></i>
          <span className="hidden sm:inline">RadioFrecuenciaWeb</span>
        </button>
        <button
          onClick={() => navigate('/pricing')}
          className="text-xs text-foreground-400 hover:text-foreground-600 cursor-pointer transition-colors"
        >
          Ver planes
        </button>
      </div>

      {/* Main content */}
      <div className="flex-1 flex items-start justify-center px-4 pb-16 pt-4">
        <div className="w-full max-w-2xl">
          <StepIndicator currentStep={currentStep} />

          {/* Card */}
          <div className="bg-background-50 rounded-2xl border border-background-200 overflow-hidden">
            {/* Step header */}
            <div className="px-6 md:px-8 py-5 border-b border-background-100">
              <p className="text-[11px] font-semibold text-foreground-400 uppercase tracking-wider mb-1">
                Paso {currentStep} de 5
              </p>
              <h2 className="text-lg md:text-xl font-bold text-foreground-950">
                {currentStepLabel}
              </h2>
            </div>

            {/* Step body */}
            <div className="px-6 md:px-8 py-6">{renderStep()}</div>

            {/* Step footer */}
            <div className="px-6 md:px-8 py-4 border-t border-background-200 flex items-center justify-between bg-background-100/70">
              <div>
                {currentStep > 1 && (
                  <button
                    type="button"
                    onClick={handleBack}
                    className="px-4 py-2.5 rounded-xl text-sm font-semibold text-foreground-600 hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
                  >
                    <i className="ri-arrow-left-line mr-1.5"></i>
                    Anterior
                  </button>
                )}
              </div>
              <div className="flex items-center gap-3">
                <span className="text-[11px] text-foreground-400 hidden sm:block">
                  {currentStep}/5
                </span>
                {isLastStep ? (
                  <button
                    type="button"
                    onClick={handleSubmit}
                    className="px-6 py-2.5 rounded-xl text-sm font-bold bg-primary-500 hover:bg-primary-600 text-background-50 cursor-pointer transition-all whitespace-nowrap shadow-lg shadow-primary-500/20"
                  >
                    {isFree ? 'Enviar solicitud' : 'Activar mi radio'}
                    <i className="ri-check-line ml-2"></i>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleNext}
                    className="px-6 py-2.5 rounded-xl text-sm font-semibold bg-primary-500 hover:bg-primary-600 text-background-50 cursor-pointer transition-all whitespace-nowrap shadow-lg shadow-primary-500/20"
                  >
                    Siguiente
                    <i className="ri-arrow-right-line ml-1.5"></i>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Quick nav to plan */}
          {currentStep === 1 && (
            <p className="text-center mt-4 text-[11px] text-foreground-400">
              ¿Ya sabés qué plan querés?{' '}
              <button
                type="button"
                onClick={() => setCurrentStep(4)}
                className="text-primary-600 hover:text-primary-700 font-semibold cursor-pointer transition-colors"
              >
                Ir directo al plan
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}